<template>
  <MasterDetail
    :emptyStateMessage="$t(`empty_states.${scope}.message`)"
    :emptyStateTitle="$t('empty_states.no_inspections')"
    :items="inspections"
    :loading="loading"
    :pageTitle="$t('nav.inspections')"
    :searchKeys="['inspected_by.full_name', 'id', 'date', 'inspection_type']"
    :searchPlaceholder="$t('ships.search_inspections')"
    :showDetail="!isIndex"
    :sortByOptions="sortByOptions(I18n)"
    :tabs="tabs"
    @newInspection="newInspectionModal()"
  >
    <template #mobileActions>
      <MobileNewInspection
        v-if="currentShip && currentShip.policy.inspect"
        @click="newInspectionModal"
      />
    </template>

    <template #item="{ item }">
      <InspectionCard :inspection="item" />
    </template>

    <template #actions>
      <NewInspection
        v-if="currentShip && currentShip.policy.inspect"
        @open="newInspectionModal"
      />
    </template>

    <template #detail>
      <router-view
        @deleteInspection="deleteInspection"
        @statusChange="updateStatus"
        @updateInspection="updateInspection"
      />
    </template>
  </MasterDetail>

  <InspectionForm
    v-model:modalIsOpen="modalIsOpen"
    :initialInspection="newInspection"
    @save="createInspection"
  />
</template>

<script setup>
import { ref, onMounted, watch, computed, inject } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import I18n from 'i18n-js';

// Utils
import useAjax from '@/functions/use_ajax';
import { sortByOptions } from '@/functions/inspections';

// Components
import MasterDetail from '@/components/generic/master_detail.vue';
import InspectionCard from '@/components/ships/inspections/inspection_card.vue';
import InspectionForm from '@/components/inspections/inspection_form.vue';
import NewInspection from '@/components/inspections/new_inspection_action.vue';
import MobileNewInspection from '@/components/inspections/new_inspection_mobile_action.vue';

const props = defineProps({
  organisationId: { type: String, required: true },
  shipId: { type: String, required: true },
  scope: { type: String, default: 'inspections' },
});

const ajax = useAjax();
const currentShip = inject('currentShip');

const tabs = computed(() => [
  {
    count: provisionalInspectionsCount.value,
    label: I18n.t('inspections.provisional_count'),
    to: { name: 'shipInspections', params: { scope: 'inspections' } },
  },
  {
    count: approvedInspectionsCount.value,
    label: I18n.t('inspections.approved_count'),
    to: { name: 'shipInspections', params: { scope: 'approved_inspections' } },
  },
]);

const newInspection = {
  date: new Date(),
  inspection_type: 'annual',
  professional_engineer: false,
  trusted_user: false
};

const inspections = ref([]);
const loading = ref(true);
const modalIsOpen = ref(false);
const route = useRoute();
const router = useRouter();
const provisionalInspectionsCount = ref(0);
const approvedInspectionsCount = ref(0);

const inspectionsURL = computed(() => {
  return `/organisations/${props.organisationId}/ships/${props.shipId}/${props.scope}`;
});

const isIndex = computed(() => {
  return route.name === 'shipInspections';
});

onMounted(loadInspections);

watch(() => props.scope, (value, oldValue) => {
  if (inspections.value && value === oldValue) return;
  loadInspections();
  }
);

async function loadInspections() {
  loading.value = true;

  let { ok, data } = await ajax(inspectionsURL.value);

  if (ok) {
    inspections.value = data.inspections;
    provisionalInspectionsCount.value = data.inspections_count;
    approvedInspectionsCount.value = data.approved_count;
  }

  loading.value = false;
}

/**
 * Displays the new inspection modal
 */
function newInspectionModal() {
  newInspection.trusted_user = currentShip.value?.trusted_user;
  modalIsOpen.value = true;
}

/**
 * Function for an emitted event from inspection details.
 *  updates the status.
 */
function updateStatus({ id, status }) {
  let inspection = inspections.value.find((i) => i.id === id);
  let scope = props.scope;

  if (inspection.status === 'approved' && status !== 'approved') {
    provisionalInspectionsCount.value++;
    approvedInspectionsCount.value--;
    scope = 'inspections';
  } else if (inspection.status !== 'approved' && status === 'approved') {
    provisionalInspectionsCount.value--;
    approvedInspectionsCount.value++;
    scope = 'approved_inspections';
  }

  inspection.status = status;

  router.replace({ params: { scope } });
}

/**
 * Function for an emitted event from inspection details.
 *  updates the inspection.
 */
function updateInspection(inspection) {
  inspections.value = inspections.value.map(i => {
    if (i.id === inspection.id) {
      return inspection;
    } else {
      return i;
    }
  });
}

/**
 * Function for an emitted event from inspection details.
 *  deletes an inspection.
 */
function deleteInspection() {
  loadInspections();
}

/**
 * Submit new inspection
 */
async function createInspection(inspection) {
  provisionalInspectionsCount.value++;
  inspections.value = [...inspections.value, inspection];

  router.push({
    name: 'shipInspection',
    params: { inspectionId: inspection.id, scope: 'inspections' },
  });
}
</script>
